/**
 * DijitalRozetConsentSection.js — öğrencinin T.C. Sanayi ve Teknoloji
 * Bakanlığı Dijital Rozet sistemine veri paylaşım rızasını yönettiği bölüm.
 * ProfilePage.js'in "Gizlilik" sekmesinde render edilir.
 *
 * Deseni (loading spinner, error+retry, optimistic toggle, toast) BİREBİR
 * NotificationPreferencesSection.js'ten alır — yeni desen icat edilmedi.
 *
 * API sözleşmesi (backend-dev üretti, routes/auth.py'de doğrulandı):
 *   GET /users/me/dijital-rozet-consent → {consent, updated_at, text_version}
 *   PUT /users/me/dijital-rozet-consent body {consent: bool} (ZORUNLU,
 *        kısmi güncelleme yok) → {consent, updated_at, text_version,
 *        certificates_reset_to_awaiting_consent, certificates_queued_for_revocation}
 *
 * Rıza KULLANICI seviyesinde tutulur (users.dr_share_consent) — sertifika
 * bazında DEĞİL. Tek toggle öğrencinin tüm sertifikalarını kapsar.
 *
 * ⚠️ TASLAK METIN: Aşağıdaki i18n anahtarlarının (profile.drConsent.*)
 * karşılıkları LanguageContext.js'te "TASLAK METIN — KVKK hukuki
 * incelemesi bekliyor" notuyla işaretli. Prod'a çıkmadan önce hukuk onayı
 * ZORUNLU. Ekranda kullanıcıya "[TASLAK]" YAZILMAZ — bu sadece kod yorumu
 * üzerinden takip edilir.
 *
 * Varsayılan KAPALI — backend hiç ayarlanmamış kullanıcı için consent:false
 * döner (bkz. routes/auth.py::get_dijital_rozet_consent). Önceden işaretli
 * kutu / karanlık desen YOK.
 *
 * Açma/kapama asimetrisi (KVKK gereği): açma doğrudan PUT gönderir; kapama
 * önce AlertDialog onayı ister — onaylanırsa PUT gider, vazgeçilirse (ya da
 * dialog Escape/dış tıklama ile kapatılırsa) toggle eski (açık) haline döner.
 *
 * Hesap silme istisna notu (Faz 7.2, security review): Bu bölümde ayrıca
 * "hesabını silersen rozetine ne olur" bilgi kartı HER ZAMAN gösterilir —
 * consent açık/kapalı olsun, kullanıcı tercihini hiç ayarlamamış olsun fark
 * etmez. Gerekçe: web'de bugün itibarıyla self-servis "hesabımı sil"
 * ekranı/akışı YOK (DELETE /users/me backend'de var ama hiçbir frontend
 * yüzeyi çağırmıyor — grep ile doğrulandı); bu yüzden istisna metni, DR
 * verisiyle ilgili tek gerçek onay yüzeyi olan bu sekmede, koşulsuz ve daima
 * görünür şekilde yer alıyor (bkz. Faz 7.2 raporu — "herkese göster" kararı).
 * Yeni bir API çağrısı / state / onay akışı İÇERMEZ — salt bilgilendirme.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BadgeCheck, Info, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { Switch } from '../ui/switch';
import { DR_DATA_ITEMS } from '../../constants/dijitalRozetDataItems';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DijitalRozetConsentSection() {
  const { authHeaders } = useAuth();
  const { lang, t } = useLanguage();
  const toast = useToast();

  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchConsent = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await axios.get(`${API}/users/me/dijital-rozet-consent`, { headers: authHeaders() });
      setConsent(!!r.data?.consent);
      setUpdatedAt(r.data?.updated_at || null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchConsent(); }, [fetchConsent]);

  // Ortak PUT çağrısı — hem açma hem geri çekme onayından sonra kullanılır.
  // Başarılı/başarısız durumu boolean olarak döner, revert kararı çağırana ait.
  const putConsent = useCallback(async (nextValue) => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/users/me/dijital-rozet-consent`,
        { consent: nextValue },
        { headers: authHeaders() }
      );
      setConsent(!!r.data?.consent);
      setUpdatedAt(r.data?.updated_at || new Date().toISOString());
      toast.success(nextValue ? t('profile.drConsent.saveSuccessOn') : t('profile.drConsent.saveSuccessOff'));
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('profile.drConsent.saveError'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [authHeaders, lang, t, toast]);

  const handleToggle = async (nextValue) => {
    if (saving) return;
    if (nextValue) {
      // Açma: doğrudan PUT, onay ekranı gerekmez.
      setConsent(true); // iyimser güncelleme
      const ok = await putConsent(true);
      if (!ok) setConsent(false); // geri al
    } else {
      // Kapama: switch görsel olarak hemen kapanır, onay dialog'u açılır.
      // Dialog onaylanırsa handleConfirmWithdraw PUT'u gönderir; vazgeçilirse
      // (ya da Escape/dış tıklama) handleCancelWithdraw toggle'ı geri açar.
      setConsent(false);
      setConfirmOpen(true);
    }
  };

  const handleConfirmWithdraw = async () => {
    const ok = await putConsent(false);
    setConfirmOpen(false);
    if (!ok) setConsent(true); // geri al
  };

  const handleCancelWithdraw = () => {
    setConfirmOpen(false);
    setConsent(true); // eski (açık) haline dön
  };

  if (loading) {
    return (
      <div className="card-clay p-8 flex items-center justify-center" data-testid="dr-consent-loading">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-clay p-6 text-center" data-testid="dr-consent-error">
        <BadgeCheck size={32} className="mx-auto text-[var(--text-muted)] mb-2" />
        <p className="font-bold text-sm text-[var(--text-muted)] mb-3">{t('profile.drConsent.loadError')}</p>
        <button
          type="button"
          onClick={fetchConsent}
          className="btn-tactile px-4 py-2 text-sm bg-white border-[var(--border-light)] inline-flex items-center gap-1.5"
          data-testid="dr-consent-retry"
        >
          <RefreshCw size={14} /> {t('profile.drConsent.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dr-consent-section">
      <div className="card-clay p-5">
        <div className="flex items-center gap-2 mb-1">
          <BadgeCheck size={18} className="text-[var(--brand-primary)]" />
          <h3 className="font-bold text-sm">{t('profile.drConsent.title')}</h3>
        </div>
        <p className="text-xs text-[var(--text-muted)]">{t('profile.drConsent.subtitle')}</p>
      </div>

      <div className="card-clay p-5">
        <p className="text-xs text-[var(--text-muted)] mb-3">{t('profile.drConsent.description')}</p>

        <ul className="space-y-1.5 mb-4" data-testid="dr-consent-data-items">
          {DR_DATA_ITEMS.map(({ icon: Icon, key }) => (
            <li key={key} className="flex items-center gap-2 text-xs font-semibold text-[var(--text-main)]">
              <Icon size={14} className="text-[var(--brand-primary)] shrink-0" />
              {t(key)}
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-3 py-2 border-t border-[var(--border-light)] pt-3" data-testid="dr-consent-toggle">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
              <BadgeCheck size={16} />
            </div>
            <span className="font-semibold text-sm text-[var(--text-main)]">{t('profile.drConsent.toggleLabel')}</span>
          </div>
          <Switch
            checked={consent}
            disabled={saving}
            onCheckedChange={handleToggle}
            aria-label={t('profile.drConsent.toggleLabel')}
            data-testid="dr-consent-switch"
          />
        </div>

        <p className="text-[11px] text-[var(--text-muted)] mt-3 flex items-start gap-1.5">
          <Info size={12} className="shrink-0 mt-0.5" />
          {t('profile.drConsent.note')}
        </p>
      </div>

      {/* Hesap silme istisnası — her zaman görünür, consent durumundan bağımsız (bkz. dosya üstü not) */}
      <div className="card-clay p-5 border-amber-200" data-testid="dr-consent-account-deletion-note">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <AlertCircle size={16} />
          </div>
          <div className="min-w-0">
            <h4 className="font-bold text-sm text-[var(--text-main)] mb-1">
              {t('profile.drConsent.accountDeletionTitle')}
            </h4>
            <p className="text-xs text-[var(--text-muted)]">
              {t('profile.drConsent.accountDeletionBody')}
            </p>
          </div>
        </div>
      </div>

      {updatedAt && (
        <p className="text-[10px] text-[var(--text-muted)] text-right" data-testid="dr-consent-updated-at">
          {t('profile.drConsent.updatedAt').replace('{{date}}', new Date(updatedAt).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US'))}
        </p>
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => { if (!open) handleCancelWithdraw(); }}
      >
        <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md" data-testid="dr-consent-withdraw-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-[var(--text-main)]">
              {t('profile.drConsent.withdrawTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[var(--text-muted)]">
              {t('profile.drConsent.withdrawBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-2">
            <AlertDialogCancel
              className="rounded-xl border-2 border-[var(--border-light)] font-bold"
              disabled={saving}
              data-testid="dr-consent-withdraw-cancel"
            >
              {t('profile.drConsent.withdrawCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl font-bold bg-[var(--brand-danger)] hover:bg-[var(--brand-danger)]/90 text-white border-b-4 border-[var(--brand-danger-shadow)]"
              disabled={saving}
              onClick={(e) => { e.preventDefault(); handleConfirmWithdraw(); }}
              data-testid="dr-consent-withdraw-confirm"
            >
              {saving ? '...' : t('profile.drConsent.withdrawConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
